import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavBadges } from '../hooks/useNavBadges';
import ErrorBoundary from './ErrorBoundary';
import AppSidebar from './AppSidebar';
import NotificationBell from './NotificationBell';
import { Icons, Avatar, LanguageToggle } from '../shared/ui';

// ── route → i18n-key map for page title + breadcrumb ──────────────────────────
//
// Values are i18n key strings (resolved at render time via t()). Title is a
// single key; crumbs is an ordered list of keys that form the breadcrumb path.

const PAGE_KEYS = {
  '/manager/dashboard':           { titleKey: 'common:nav.dashboard',     crumbKeys: ['common:nav.home'] },
  '/manager/finance':             { titleKey: 'common:nav.finance',       crumbKeys: ['common:section.finance'] },
  '/manager/payment-management':  { titleKey: 'common:nav.payments',      crumbKeys: ['common:section.finance', 'common:nav.payments'] },
  '/manager/meter-readings':      { titleKey: 'common:nav.meters',        crumbKeys: ['common:section.finance', 'common:nav.meters'] },
  '/manager/calendar':            { titleKey: 'common:nav.calendar',      crumbKeys: ['common:section.overview', 'common:nav.calendar'] },
  '/manager/properties':          { titleKey: 'common:nav.properties',    crumbKeys: ['common:section.management', 'common:nav.properties'] },
  '/manager/property-management': { titleKey: 'common:nav.propertySetup', crumbKeys: ['common:section.management', 'common:nav.propertySetup'] },
  '/manager/users':               { titleKey: 'common:nav.staff',         crumbKeys: ['common:section.management', 'common:nav.staff'] },
  '/manager/renters':             { titleKey: 'common:nav.renters',       crumbKeys: ['common:section.management', 'common:nav.renters'] },
  '/manager/vehicles':            { titleKey: 'common:nav.vehicles',      crumbKeys: ['common:section.management', 'common:nav.vehicles'] },
  '/manager/contracts':           { titleKey: 'common:nav.contracts',     crumbKeys: ['common:section.management', 'common:nav.contracts'] },
  '/manager/tickets':             { titleKey: 'common:nav.tickets',       crumbKeys: ['common:section.management', 'common:nav.tickets'] },
  '/manager/violations':          { titleKey: 'common:nav.violations',    crumbKeys: ['common:section.management', 'common:nav.violations'] },
  '/manager/cv-monitoring':       { titleKey: 'common:nav.cvMonitoring',  crumbKeys: ['common:section.monitoring', 'common:nav.cvMonitoring'] },
  '/manager/cv-setup':            { titleKey: 'common:nav.cvSetup',       crumbKeys: ['common:section.monitoring', 'common:nav.cvSetup'] },
  '/manager/settings':            { titleKey: 'common:nav.settings',      crumbKeys: ['common:nav.settings'] },
  '/manager/my-payments':         { titleKey: 'common:nav.payments',      crumbKeys: ['common:nav.myPayments'] },
  '/manager/my-meters':           { titleKey: 'common:nav.myMeters',      crumbKeys: ['common:nav.myMeters'] },
  '/manager/my-contract':         { titleKey: 'common:nav.contracts',     crumbKeys: ['common:nav.myContract'] },
};

// ── shell ──────────────────────────────────────────────────────────────────────
export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isConnected } = useNotifications();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const badges = useNavBadges();

  const keys = PAGE_KEYS[pathname] ?? { titleKey: 'common:nav.dashboard', crumbKeys: ['common:nav.home'] };
  const pageTitle = t(keys.titleKey);
  const crumbs = keys.crumbKeys.map((k) => t(k));

  const fullName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.username || t('user.guest');

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-stone-900/40 z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <AppSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        badges={badges}
      />

      {/* Content — offset by sidebar width */}
      <div className="lg:pl-[232px] min-w-0">
        {/* Topbar — sticky to viewport; must NOT be inside an overflow:hidden ancestor */}
        <header className="sticky top-0 z-30 bg-white border-b border-stone-200">
          <div className="flex items-center justify-between h-[52px] px-4 sm:px-5">

            {/* Left: hamburger + breadcrumb */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-1.5 rounded-lg text-stone-500 hover:text-stone-700 hover:bg-stone-100 transition-colors flex-shrink-0"
              >
                <Icons.Menu size={18} />
              </button>

              <nav className="hidden sm:flex items-center gap-1 text-[13px] min-w-0" aria-label="Breadcrumb">
                {crumbs.map((c, i) => (
                  <React.Fragment key={c + i}>
                    {i > 0 && (
                      <Icons.ChevronRight size={12} className="text-stone-300 flex-shrink-0 mx-0.5" />
                    )}
                    <span
                      className={
                        i === crumbs.length - 1
                          ? 'font-semibold text-stone-800 truncate'
                          : 'text-stone-400 truncate'
                      }
                    >
                      {i === 0 && crumbs.length === 1 ? pageTitle : c}
                    </span>
                  </React.Fragment>
                ))}
              </nav>

              {/* Mobile: just page title */}
              <span className="sm:hidden text-sm font-semibold text-stone-900 truncate">
                {pageTitle}
              </span>
            </div>

            {/* Right: lang · status · bell · avatar */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <LanguageToggle />

              <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-50 border border-stone-200">
                <span
                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    isConnected ? 'bg-teal-500' : 'bg-red-400'
                  }`}
                />
                <span className="text-[11px] text-stone-500 font-medium">
                  {isConnected ? t('user.online') : t('user.offline')}
                </span>
              </div>

              <NotificationBell />

              {/* Profile */}
              <div className="relative">
                <button
                  onClick={() => setProfileOpen((p) => !p)}
                  className="flex items-center gap-2 p-1 pr-2 rounded-xl hover:bg-stone-100 transition-colors"
                >
                  <Avatar name={fullName} size={28} />
                  <span className="hidden md:block text-[11px] font-bold text-stone-700 uppercase tracking-wide">
                    {fullName.split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </span>
                </button>

                {profileOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border border-stone-200 shadow-popover z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-stone-100">
                        <p className="text-sm font-semibold text-stone-900">{fullName}</p>
                        <p className="text-xs text-stone-500">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/manager/settings'); setProfileOpen(false); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2 text-[13px] text-stone-700 hover:bg-stone-50 transition-colors"
                        >
                          <Icons.Cog size={14} className="text-stone-400" />
                          {t('nav.settings')}
                        </button>
                      </div>
                      <div className="border-t border-stone-100">
                        <button
                          onClick={logout}
                          className="flex items-center gap-2.5 w-full px-4 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Icons.LogOut size={14} />
                          {t('auth.signOut')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </header>

        {/* Page content */}
        <main className="overflow-x-hidden">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
